<section>
  <span class="d-block text-md mb-2">{{ 'wizards:entityWizards.company.templatesStep.description' | i18next }}</span>

  @if (!destination() && !hasManyDestinations()) {
    <span class="d-block text-md mb-2 fw-semibold">
      {{
        (hasOnlyAzureVmDestinations()
          ? 'wizards:entityWizards.company.templatesStep.azureVmOnlyHint'
          : 'wizards:entityWizards.company.templatesStep.noDestinationHint'
        ) | i18next
      }}
    </span>
  }

  @if (hasManyDestinations()) {
    <span class="d-block text-md mb-2 fw-semibold">
      {{ 'wizards:entityWizards.company.templatesStep.selectDestinationHint' | i18next }}
    </span>
  }

  <span class="d-block text-md mb-3 fw-semibold">{{ 'wizards:entityWizards.company.destinationsStep.skipHint' | i18next }}</span>

  @let ffiResetPlans = ffiResetPlanNames();

  @if (ffiResetPlans.length) {
    <mbs-alert
      class="mb-0"
      [icon]="true"
      [type]="alertType.warning">
      {{ 'wizards:entityWizards.company.templatesStep.ffiScheduleReset' | i18next: { plans: ffiResetPlans.join(', ') } }}
    </mbs-alert>
  }

  @for (template of sortedTemplates(); track template.id) {
    @let expanded = expandedTemplateId() === template.id;
    @let problems = templateProblems().get(template.id);

    <article
      class="border rounded cursor-pointer p-3 mt-3"
      [class.bg-blue-10]="expanded"
      [class.border-danger]="!!problems"
      [class.border-primary]="expanded && !problems"
      (click)="toggleTemplateHandler(template)">
      <div class="form-row align-items-center">
        <div class="col-auto">
          <div class="mbs-company-backup-template_icon">
            <div class="d-flex align-items-center justify-content-center rounded-circle w-100 h-100 bg-info-30">
              <span
                class="ico text-xxl"
                [class.ico-file]="!template.isImageBased"
                [class.ico-imagebased]="template.isImageBased"></span>
            </div>
          </div>
        </div>

        <div class="col">
          <mbs-button
            class="d-block"
            [icon]="expanded ? 'fa fa-caret-up' : 'fa fa-caret-down'"
            [id]="'company-template-toggle-' + template.id"
            [isCtrl]="true"
            (click)="toggleTemplateHandler(template)">
            <span class="fw-semibold">{{ template.name }}</span>
          </mbs-button>

          <div class="form-row align-items-center mt-1">
            <div class="col-auto pr-0">
              <span class="ico ico-subdirectory-arrow text-sm text-gray-600"></span>
            </div>

            <div class="col-auto">
              <mbs-tag
                class="bg-white text-nowrap"
                [clickable]="true"
                [closable]="false"
                (click)="$event.stopPropagation()"
                (mbsClick)="editDestinationClickHandler(template)">
                @if (templateDestination(template); as selected) {
                  <span class="d-flex align-items-center">
                    <span
                      class="flex-shrink-0 mr-2 mbs-storage mbs-storage-sm mbs-storage_{{
                        selected.storageAccountType | storageIcon
                      }}"></span>
                    {{ selected.displayDestinationName }}
                  </span>
                } @else {
                  {{ 'wizards:entityWizards.company.templatesStep.notSelected' | i18next }}
                }
              </mbs-tag>
            </div>
          </div>
        </div>

        <div
          class="col-auto"
          (click)="$event.stopPropagation()">
          <mbs-switcher
            [boldLabel]="false"
            [id]="'company-template-create-' + template.id"
            [label]="
              (template.create && templateDestination(template)
                ? 'wizards:entityWizards.company.templatesStep.create'
                : 'wizards:entityWizards.company.templatesStep.skip'
              ) | i18next
            "
            [ngModel]="template.create && !!templateDestination(template)"
            (beforeUpdate)="beforeCreateChangeHandler(template, $event)"
            (ngModelChange)="createChangeHandler(template, $event)"></mbs-switcher>
        </div>
      </div>

      @if (problems) {
        <mbs-alert
          class="mt-3 mb-0"
          [icon]="true"
          [type]="alertType.danger">
          @for (problem of problems; track problem) {
            <div>{{ 'wizards:entityWizards.company.templatesStep.invalidTemplates.problems.' + problem | i18next }}</div>
          }
        </mbs-alert>
      }

      @if (expanded) {
        <dl class="row align-items-center mb-0 mt-3">
          <dt class="col-3 text-gray-600 fw-normal">
            {{ 'wizards:entityWizards.company.templatesStep.source' | i18next }}
          </dt>
          <dd class="col-9 mb-2">
            <div class="d-flex align-items-center">
              <div
                class="overflow-hidden"
                (click)="$event.stopPropagation()">
                <mbs-tag
                  class="bg-white"
                  [clickable]="true"
                  [closable]="false"
                  [tooltip]="templateSource(template)"
                  (mbsClick)="editTemplateFieldClickHandler(template, 'source')">
                  {{ templateSource(template) }}
                </mbs-tag>
              </div>
            </div>
          </dd>

          <dt class="col-3 text-gray-600 fw-normal">
            {{ 'wizards:entityWizards.company.templatesStep.schedule' | i18next }}
          </dt>
          <dd class="col-9 mb-2">
            @let canEditSchedule = scheduleEditable(template);

            <div class="d-flex align-items-center">
              <div
                class="overflow-hidden"
                (click)="$event.stopPropagation()">
                <mbs-tag
                  [class]="canEditSchedule ? 'bg-white' : 'bg-gray-10 text-gray-600'"
                  [clickable]="canEditSchedule"
                  [closable]="false"
                  [disabled]="!canEditSchedule"
                  [tooltip]="template.schedule"
                  (mbsClick)="editTemplateFieldClickHandler(template, 'schedule')">
                  {{ template.schedule }}
                </mbs-tag>
              </div>

              @if (!canEditSchedule) {
                <span
                  class="cursor-help fa fa-info-circle text-info ml-2 flex-shrink-0"
                  container="body"
                  [ngbTooltip]="'wizards:entityWizards.company.templatesStep.scheduleNeedsDestination' | i18next"
                  (click)="$event.stopPropagation()"></span>
              }
            </div>
          </dd>

          <dt class="col-3 text-gray-600 fw-normal">
            {{ 'wizards:entityWizards.company.templatesStep.compression' | i18next }}
          </dt>
          <dd class="col-9 mb-2">
            <mbs-tag
              class="bg-white"
              [clickable]="true"
              [closable]="false"
              (click)="$event.stopPropagation()"
              (mbsClick)="editTemplateFieldClickHandler(template, 'compression')">
              {{
                (template.compressionEncryptionStep.UseCompression
                  ? 'wizards:entityWizards.company.templatesStep.enabled'
                  : 'wizards:entityWizards.company.templatesStep.disabled'
                ) | i18next
              }}
            </mbs-tag>
          </dd>

          <dt class="col-3 text-gray-600 fw-normal">
            {{ 'wizards:entityWizards.company.templatesStep.encryption' | i18next }}
          </dt>
          <dd class="col-9 mb-2">
            <mbs-tag
              class="bg-white"
              [clickable]="true"
              [closable]="false"
              (click)="$event.stopPropagation()"
              (mbsClick)="editTemplateFieldClickHandler(template, 'encryption')">
              {{
                (template.compressionEncryptionStep.UseEncryption
                  ? 'wizards:entityWizards.company.templatesStep.enabled'
                  : 'wizards:entityWizards.company.templatesStep.disabled'
                ) | i18next
              }}
            </mbs-tag>
          </dd>

          <dt class="col-3 text-gray-600 fw-normal">
            {{ 'wizards:entityWizards.company.templatesStep.applyTo' | i18next }}
          </dt>
          <dd class="col-9 mb-0">{{ template.applyTo }}</dd>
        </dl>
      }
    </article>
  }
</section>

<ng-template #invalidTemplatesBody>
  <p>{{ 'wizards:entityWizards.company.templatesStep.invalidTemplates.message' | i18next }}</p>
  <ul class="mb-0">
    @for (template of invalidTemplates(); track template.id) {
      <li>
        <span class="fw-semibold">{{ template.name }}</span>
        <ul>
          @for (problem of templateProblems().get(template.id); track problem) {
            <li>{{ 'wizards:entityWizards.company.templatesStep.invalidTemplates.problems.' + problem | i18next }}</li>
          }
        </ul>
      </li>
    }
  </ul>
</ng-template>
